/**
 * yieldToMain — break a long task without giving up our place in line
 * (settlement study R4, 2026-09-02).
 *
 * `setTimeout(0)` parks the continuation at the BACK of the task queue behind
 * every pending timer and message (a streaming city can hold hundreds), so a
 * time-sliced loop that yields between slices loses whole frames per yield.
 * `scheduler.yield()` (Chrome 129+, all majors by 2025) yields to input and
 * rendering but resumes the continuation with priority. Falls back to a
 * macrotask where the API is missing (headless harnesses, older browsers).
 */

interface SchedulerLike { yield?: () => Promise<void> }

export function yieldToMain(): Promise<void> {
  const s = (globalThis as { scheduler?: SchedulerLike }).scheduler;
  if (s && typeof s.yield === 'function') {
    try { return s.yield(); } catch { /* fall through */ }
  }
  return new Promise<void>((r) => setTimeout(r, 0));
}

/** True when the browser has the prioritised yield (for diagnostics). */
export function hasSchedulerYield(): boolean {
  const s = (globalThis as { scheduler?: SchedulerLike }).scheduler;
  return !!s && typeof s.yield === 'function';
}
